import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './student-dashboard-responsive.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STUDENT_ASSIGNED_QUIZZES_API = process.env.REACT_APP_STUDENT_ASSIGNED_QUIZZES_API || `${API_BASE_URL}/api/admin-app/student-assigned-quizzes/`;
const STUDENT_ATTENDED_HISTORY_API = process.env.REACT_APP_STUDENT_ATTENDED_HISTORY_API || `${API_BASE_URL}/api/admin-app/institution-student-attended-history/`;

const dashboardConfig = {
    student: {
        title: 'Student Dashboard',
        subtitle: 'Track aptitude prep progress, upcoming sessions, and performance insights from one place.',
    },
    institution: {
        title: 'Institution Dashboard',
        subtitle: 'Monitor student readiness, engagement trends, and institutional performance at a glance.',
    },
};

const DashboardMain = ({ role }) => {
    const activeRole = role === 'institution' ? 'institution' : 'student';
    const config = dashboardConfig[activeRole];
    const [assignedQuizzes, setAssignedQuizzes] = useState([]);
    const [isLoadingAssignedQuizzes, setIsLoadingAssignedQuizzes] = useState(false);
    const [attendedSet, setAttendedSet] = useState(new Set());

    useEffect(() => {
        if (activeRole !== 'student') {
            return;
        }

        const fetchAssignedQuizzes = async () => {
            const collegeCode = (
                localStorage.getItem('guest_college_code') ||
                sessionStorage.getItem('guest_college_code') ||
                ''
            ).trim();

            if (!collegeCode) {
                setAssignedQuizzes([]);
                return;
            }

            try {
                setIsLoadingAssignedQuizzes(true);
                const response = await fetch(`${STUDENT_ASSIGNED_QUIZZES_API}?college_code=${encodeURIComponent(collegeCode)}`);

                if (!response.ok) {
                    setAssignedQuizzes([]);
                    return;
                }

                const data = await response.json();
                setAssignedQuizzes(Array.isArray(data) ? data : []);
            } catch (error) {
                setAssignedQuizzes([]);
            } finally {
                setIsLoadingAssignedQuizzes(false);
            }
        };

        const fetchAttendedQuizIds = async () => {
            const collegeCode = (
                localStorage.getItem('guest_college_code') ||
                sessionStorage.getItem('guest_college_code') ||
                ''
            ).trim();
            const studentId = (localStorage.getItem('guest_student_id') || sessionStorage.getItem('guest_student_id') || '').trim();
            const studentEmail = (localStorage.getItem('guest_student_email') || sessionStorage.getItem('guest_student_email') || '').trim();

            if (!collegeCode) {
                setAttendedSet(new Set());
                return;
            }

            try {
                const params = new URLSearchParams({ college_code: collegeCode });
                if (studentId) params.set('student_id', studentId);
                else if (studentEmail) params.set('student_email', studentEmail);

                const response = await fetch(`${STUDENT_ATTENDED_HISTORY_API}?${params.toString()}`);
                if (!response.ok) {
                    setAttendedSet(new Set());
                    return;
                }

                const data = await response.json();
                const ids = Array.isArray(data) ? data.map((row) => String(row.test_id)) : [];
                setAttendedSet(new Set(ids));
            } catch (error) {
                setAttendedSet(new Set());
            }
        };

        fetchAssignedQuizzes();
        fetchAttendedQuizIds();
    }, [activeRole]);

    return (
        <div className="pt---120 pb---120">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="login-right-form">
                            <div className="login-top">
                                <h3>{config.title}</h3>
                            </div>

                            {activeRole === 'student' && (
                                <>
                                    {/* Device Test Section */}
                                    <div className="mt-4 student-dashboard-tools">
                                        <div className="student-dashboard-tile-grid">
                                            <div className="student-test-tile">
                                                <div>
                                                    <span className="student-test-tile-title">Run Device Test</span>
                                                    <span className="student-test-tile-copy">Camera + Microphone compatibility</span>
                                                </div>
                                                <Link
                                                    to="/dashboard/student/device-test?role=student"
                                                    className="btn btn-primary btn-block mt-3"
                                                >
                                                    Run Test
                                                </Link>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quiz Section */}
                                    <div className="mt-5 student-dashboard-tools">
                                        <div className="student-quiz-section-header">
                                            <h3 className="student-quiz-section-title">Assigned Quizzes</h3>
                                            <p className="student-quiz-section-subtitle">Take your assigned quizzes to assess your aptitude and readiness.</p>
                                        </div>

                                        {isLoadingAssignedQuizzes ? (
                                            <div className="student-dashboard-tile-grid mt-4">
                                                <div className="student-test-tile">
                                                    <span className="student-test-tile-title">Loading Assigned Quizzes...</span>
                                                </div>
                                            </div>
                                        ) : assignedQuizzes.length === 0 || (assignedQuizzes.filter(q => !attendedSet.has(String(q.test_id))).length === 0) ? (
                                            <div className="student-dashboard-tile-grid mt-4">
                                                <div className="student-test-tile">
                                                    <span className="student-test-tile-title">No Assigned Quizzes</span>
                                                    <span className="student-test-tile-copy">Your institution has not assigned a quiz yet.</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="student-dashboard-tile-grid mt-4">
                                                {assignedQuizzes.filter(q => !attendedSet.has(String(q.test_id))).map((quiz) => (
                                                    <div className="student-test-tile" key={quiz.id}>
                                                        <span className="student-test-tile-title">Quiz #{quiz.test_id}</span>
                                                        <div className="student-test-tile-content">
                                                            <span className="student-test-tile-copy"><strong>Questions:</strong> {quiz.selected_question_count}</span>
                                                            <span className="student-test-tile-copy"><strong>Marks:</strong> {quiz.total_marks}</span>
                                                            <span className="student-test-tile-copy"><strong>Assigned:</strong> {quiz.assigned_at ? new Date(quiz.assigned_at).toLocaleString() : '-'}</span>
                                                        </div>
                                                        <Link
                                                            to={`/dashboard/student/test-instructions?role=student&quiz_id=${quiz.test_id}`}
                                                            className="btn btn-primary btn-block mt-3"
                                                        >
                                                            Start Quiz
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardMain;
